/**
 * RepBuddy - Inline markdown for chat text (SCRUM-41)
 *
 * The chat model emits **bold** and `code` inside its prose, list items and
 * table cells. Without this it showed up on screen as literal asterisks and
 * backticks. Deliberately tiny: bold, italic and inline code only.
 *
 * renderProse() adds the one structural thing prose needs: paragraphs. The
 * model separates them with a blank line; a single newline is a soft break.
 * Before this, a two-paragraph answer rendered as one run-on block.
 *
 * Returns React nodes, never HTML - nothing here goes near
 * dangerouslySetInnerHTML, so model output cannot inject markup.
 */

// Order matters: `code` is matched first so markdown inside a code span is left
// alone, then bold (**) before italic (*) so ** is never read as two italics.
//
// Emphasis spans may not begin or end with a space, which is what stops
// "2 * 3 * 4" from italicising the 3. Underscores are deliberately not
// supported: _italic_ is worth far less than leaving snake_case identifiers and
// email local-parts intact, and the prompt asks the model for ** and ` anyway.
const EMPHASIS = '[^\\s*](?:[^*\\n]*[^\\s*])?';
const PATTERN = new RegExp(`(\`[^\`\\n]+\`|\\*\\*${EMPHASIS}\\*\\*|\\*${EMPHASIS}\\*)`, 'g');

export function renderInline(text, keyPrefix = 'md') {
  if (typeof text !== 'string' || text === '') return text;
  // Fast path - most chat text has no markers at all.
  if (!/[`*]/.test(text)) return text;

  const parts = text.split(PATTERN);
  return parts.map((part, i) => {
    if (!part) return null;
    const key = `${keyPrefix}-${i}`;
    if (part.length > 2 && part.startsWith('`')  && part.endsWith('`'))  return <code key={key}>{part.slice(1, -1)}</code>;
    if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (part.length > 2 && part.startsWith('*')  && part.endsWith('*'))  return <em key={key}>{part.slice(1, -1)}</em>;
    return part;
  });
}

// Prose with paragraphs. Splits on blank lines into <p> elements, turns single
// newlines inside a paragraph into <br/>, and runs renderInline on each line.
// `trailing` is appended inside the LAST paragraph so a streaming caret sits at
// the end of the text rather than on its own line below it.
export function renderProse(text, keyPrefix = 'p', { trailing = null } = {}) {
  const src = typeof text === 'string' ? text : '';
  const paragraphs = src.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  if (paragraphs.length === 0) return trailing ? <p key={`${keyPrefix}-0`}>{trailing}</p> : null;

  return paragraphs.map((para, pi) => {
    const lines = para.split('\n');
    const nodes = [];
    lines.forEach((line, li) => {
      if (li > 0) nodes.push(<br key={`${keyPrefix}-${pi}-br${li}`} />);
      nodes.push(renderInline(line, `${keyPrefix}-${pi}-${li}`));
    });
    const isLast = pi === paragraphs.length - 1;
    return <p key={`${keyPrefix}-${pi}`}>{nodes}{isLast ? trailing : null}</p>;
  });
}

/** Convenience wrapper for use directly in JSX. */
export default function InlineMarkdown({ text }) {
  return <>{renderInline(text)}</>;
}
